import { nextTick } from "vue";
import { cloneDeep, randomId } from "@/shared/utils/helpers.js";
import { copy } from "@/shared/utils/string";
import { testimonialService } from "@/apps/love/services/testimonial.service";
import { loveCartService } from "@/apps/love/services/love-cart.service";
import { uploadService } from "@/apps/love/services/upload.service";
import { wallsService } from "@/apps/love/services/walls.service";
import { EventBus } from "@/shared/projects/event-bus.ts";
import { iframeMutations } from "@/shared/store/iframe.mutations";
import { loveSnapshotService } from "@/apps/love/store/love-snapshot.service";
import { makeLogger } from "@/shared/consola";
import { t } from "@/i18n";
import {
    PlayerIcon,
    BookIcon,
    BusinessHandshakeIcon,
    CoinsHandIcon,
    UserMultipleIcon,
    CalendarIcon,
    Building2Icon,
    LayoutVertical4Icon,
    LayoutSquare2Icon,
    LayoutHorizontal2Icon,
    ChevronRightIcon,
    UserCircleSingleIcon,
    StoreIcon,
} from "@easy/ui/icons";
const logger = makeLogger("love.store");
import { WALL_LAYOUTS } from "@/apps/love/services/walls.service";

export default {
    namespaced: true,
    state: () => ({
        wall: {},
        testimonials: [],
        authorGhost: {},
        testimonial: {},
        iframe: null,
        iframeLoaded: false,
        hasChanges: false,
        hasSnapshot: false,
    }),
    getters: {
        // isFreshlyPublished: (s) => !s.hasChanges && !s.hasSnapshot,
        appUrl: (s, g, rootS, rootGetters) => rootGetters.easyUrls.love,
        askForTestimonialLink(state, getters) {
            return `${getters.appUrl}/ask/${state.wall.uuid}`;
        },
        wallLink: (state, g, rootState) => `${rootState.globalUrl}/walls/${state.wall.uuid}`,
        wallIframe: (state, g, rootState) => {
            return `<iframe
                id="${state.wall.uuid}"
                width="100%"
                src="${rootState.globalUrl}/walls/${state.wall.uuid}"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                frameborder="0"
                scrolling="no"
                ></iframe>
                <script> window.addEventListener("message", (e) => {!!e.data.frameHeight && e.data.wallUuid === "${state.wall.uuid}" && (document.getElementById("${state.wall.uuid}").style.height = e.data.frameHeight);});</script>`;
        },
        wall: (state) => state.wall,
        templates: () => [
            {
                value: t("love.templates.empty"),
                id: "empty",
            },
            {
                value: t("love.templates.product"),
                icon: StoreIcon,
                id: "product",
            },
            {
                value: "E-book",
                icon: BookIcon,
                id: "e-book",
            },
            {
                value: t("love.templates.training"),
                icon: CalendarIcon,
                id: "training",
            },
            {
                value: t("love.templates.consultations"),
                icon: BusinessHandshakeIcon,
                id: "consultations",
            },
            {
                value: t("love.templates.community"),
                icon: UserMultipleIcon,
                id: "community",
            },
            {
                value: "Webinar",
                icon: PlayerIcon,
                id: "webinar",
            },
            {
                value: t("love.templates.service"),
                icon: CoinsHandIcon,
                id: "service",
            },
            {
                value: t("love.templates.event"),
                icon: Building2Icon,
                id: "event",
            },
        ],
        layouts: () => [
            {
                value: WALL_LAYOUTS.board,
                label: t("love.wall.board"),
                info: t("love.wall.board_info"),
                icon: LayoutSquare2Icon,
            },
            {
                value: WALL_LAYOUTS.bigslider,
                label: t("love.wall.bigslider"),
                info: t("love.wall.bigslider_info"),
                icon: ChevronRightIcon,
            },
            {
                value: WALL_LAYOUTS.slider,
                label: t("love.wall.slider"),
                info: t("love.wall.slider_info"),
                icon: LayoutHorizontal2Icon,
            },
            {
                value: WALL_LAYOUTS.multislider,
                label: t("love.wall.multislider"),
                info: t("love.wall.multislider_info"),
                icon: LayoutVertical4Icon,
            },
            {
                value: WALL_LAYOUTS.avatars,
                label: t("love.wall.avatars"),
                info: t("love.wall.avatars_info"),
                icon: UserCircleSingleIcon,
            },
        ],
    },
    mutations: {
        ...iframeMutations,
        addTestimonial(state, testimonial) {
            testimonial._created = true;
            testimonial.id = randomId();
            testimonial.order = state.testimonials.length;
            testimonial.hide = false;
            state.testimonials.push(testimonial);
        },
        changeTestimonial(state, payload) {
            const updatedTestimonial = Object.assign({}, payload);

            const index = state.testimonials.findIndex((x) => x.id == payload.id);
            state.testimonials.splice(index, 1, updatedTestimonial);
        },
        removeTestimonial(state, testimonial) {
            if (testimonial._created) {
                const index = state.testimonials.map((x) => x.id).indexOf(testimonial.id);
                if (index > -1) {
                    state.testimonials.splice(index, 1);
                }
            } else {
                if (testimonial._changed) {
                    delete testimonial._changed;
                }
                testimonial["_deleted"] = true;
            }
        },
        setTestimonials(state, payload) {
            if (payload) {
                state.testimonials = loveSnapshotService.makeSnapshotState(
                    state,
                    "testimonials",
                    payload,
                    {}
                );
                state.hasChanges = true;
            }
            state.testimonials
                .filter((t) => !t._deleted)
                .forEach((t, index) => {
                    t.order = index;
                    if (!t._created && !t._deleted) {
                        t["_changed"] = true;
                    }
                });
        },
        setTestimonial(state, payload) {
            state.testimonial = payload;
        },
        confirmTestimonial(state) {
            state.testimonial.confirmed = true;
        },
        async setProject(state, { wall, testimonials, snapshot = {} }) {
            state.wall = loveSnapshotService.makeSnapshotState(state, "wall", wall, snapshot);
            state.testimonials = loveSnapshotService.makeSnapshotState(
                state,
                "testimonials",
                testimonials,
                snapshot
            );
            state.wall.author = loveSnapshotService.makeSnapshotState(
                state,
                "authorGhost",
                state.wall.author || {},
                snapshot
            );
            state.hasChanges = false;
        },
        clearProject(state) {
            state.wall = {};
            state.testimonials = [];
            loveSnapshotService.clearSnapshot(state, "wall", {});
            loveSnapshotService.clearSnapshot(state, "testimonials", []);
            loveSnapshotService.clearSnapshot(state, "authorGhost", {});
        },
        setWall(state, payload) {
            state.wall = cloneDeep(payload.wall);
        },
        acceptAllTestimonials(state) {
            state.testimonials.forEach((testimonial) => {
                testimonial.isPublished = true;
                if (!testimonial._created && !testimonial._deleted) {
                    testimonial["_changed"] = true;
                }
            });
        },
    },
    actions: {
        refreshIframe({ state, getters }) {
            const wall = cloneDeep(state.wall);
            wall.testimonials = cloneDeep(state.testimonials);
            state.iframe = document.querySelector("#iframe");
            if (state.iframe && wall.id) {
                state.iframe.contentWindow.postMessage({ wallData: wall }, getters.appUrl);
            }
        },
        async uploadAvatar({ rootState, commit }, { testimonial, file }) {
            rootState.waiting = true;
            try {
                const { avatar } = await uploadService.testimonials.avatarForExisting({
                    file,
                    id: testimonial.id,
                });
                const changedTestimonial = testimonial;
                changedTestimonial.photo = avatar;
                commit("changeTestimonial", changedTestimonial);
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async uploadAvatarNewTestimonial({ rootState }, { testimonial, file }) {
            rootState.waiting = true;
            try {
                const { avatar } = await uploadService.testimonials.avatarForNew({ file });
                testimonial.photo = avatar;
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async uploadImageTestimonial({ rootState }, { testimonial, file }) {
            rootState.waiting = true;
            try {
                const { image } = await uploadService.testimonials.image({ file });
                testimonial.images.push(image);
                rootState.waiting = false;
                return image;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async uploadVideoTestimonial({ rootState }, { testimonial, file }) {
            rootState.waiting = true;
            try {
                const { video } = await uploadService.testimonials.video({ file });
                testimonial.video = video;
                EventBus.$emit("videoUploaded");
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async uploadAuthorAvatar({ state, rootState }, { file }) {
            const { wall } = state;
            rootState.waiting = true;
            try {
                const { image } = await uploadService.walls.authorAvatar({ file, id: wall.id });
                wall.author.avatar = image;
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async uploadAskImage({ state, rootState }, { file }) {
            const { wall } = state;
            rootState.waiting = true;
            try {
                const { image } = await uploadService.walls.askImage({ file, id: wall.id });
                wall.askImg = image;
                rootState.waiting = false;
                return image;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async createNewTestimonial({ state, rootState, commit }, payload) {
            rootState.waiting = true;
            const { wall } = state;
            try {
                await testimonialService.createNew({
                    ...payload,
                    isPublished: Boolean(wall.publishAutomatically),
                });
                const modalData = {
                    type: "success",
                    title: t("love.reviews.testimonial_created"),
                    actionBtn: wall.redirectButton,

                    callback: () => {
                        window.location.href = wall.redirectUrl;
                    },
                };
                commit("showModal", modalData, { root: true });
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async removeTestimonial({ commit }, payload) {
            commit("removeTestimonial", payload);
            commit("setTestimonials");
        },
        async publishTestimonial(_, testimonial) {
            if (!testimonial._created && !testimonial._deleted) {
                testimonial["_changed"] = true;
            }
            testimonial.isPublished = true;
        },
        cloneTestimonial({ commit }, payload) {
            const clonedTestimonial = cloneDeep(payload);
            commit("addTestimonial", clonedTestimonial);
        },
        async loadTestimonialByUuid({ rootState, commit }, payload) {
            rootState.waiting = true;
            try {
                const testimonial = await testimonialService.getOneByUuid(payload);
                testimonial.gifs = JSON.parse(testimonial.gifs);
                testimonial.images = JSON.parse(testimonial.images);

                commit("setTestimonial", testimonial);
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async loadFirstWallByTestimonialUuid({ rootState, commit }, payload) {
            rootState.waiting = true;
            try {
                const wall = await testimonialService.loadFirstWallByTestimonialUuid(payload);

                commit("setWall", { wall });
                rootState.waiting = false;
            } catch {
                /* eslint-disable-next-line no-empty */
            }
        },
        async sendCreateRequest2({ state, rootState, commit }, { emails }) {
            rootState.waiting = true;

            try {
                await wallsService.requestForReview2({
                    uuid: state.wall.uuid,
                    emails,
                });
                commit(
                    "showAlert",
                    {
                        type: "success",
                        title: t("love.reviews.request_sent"),
                    },
                    { root: true }
                );
                EventBus.$emit("openSidebar", "menu");
                rootState.waiting = false;
                return true;
            } catch {
                return false;
            }
        },
        async sendConfirmationRequest({ rootState, commit }, payload) {
            rootState.waiting = true;

            const { email, content, testimonial } = payload;

            if (testimonial._created) {
                const createdTestimonial = await testimonialService.createNew(testimonial);
                testimonial.id = createdTestimonial.id;
                commit(
                    "showAlert",
                    {
                        type: "success",
                        title: t("love.reviews.request_sent"),
                    },
                    { root: true }
                );
                return;
            }

            try {
                // TODO: ask about content of the e-mail, it is NOT used for now.
                testimonialService.sendConfirmationRequest({
                    id: testimonial.id,
                    email: email || testimonial.authorEmail,
                    content,
                });
                commit(
                    "showAlert",
                    {
                        type: "success",
                        title: t("love.reviews.request_sent"),
                    },
                    { root: true }
                );
                EventBus.$emit("openSidebar", "menu");
                rootState.waiting = false;
            } catch {
                // eslint-disable-next-line no-empty
            }
        },
        async confirmTestimonial({ state, rootState, commit, dispatch }, payload) {
            rootState.waiting = true;
            const testimonial = {
                name: payload.name,
                position: payload.position,
                www: payload.www,
                content: payload.content,
                rate: payload.rate,
                hide: payload.hide,
                photo: payload.photo,
                confirmed: true,
                authorEmail: payload.authorEmail,
                order: payload.order,
                images: JSON.stringify(payload.images),
                gifs: JSON.stringify(payload.gifs),
                isPublished: payload.isPublished,
                video: payload.video,
                type: payload.type,
            };
            try {
                await testimonialService.confirm({ id: payload.id, testimonial });

                if (state.wall.webhookUrl) {
                    dispatch("sendWebhook", { action: "confirmation", testimonial: payload });
                }
                commit("confirmTestimonial");
                rootState.waiting = false;
            } catch {
                // eslint-disable-next-line no-empty
            }
        },
        showNoTestimonialsModal({ commit }) {
            commit(
                "showModal",
                {
                    type: "info",
                    title: t("love.empty.title"),
                    desc: t("love.empty.desc"),
                    cancelBtn: t("shared.cancel"),
                    actionBtn: t("love.empty.cta"),
                    callback: () => {
                        EventBus.$emit("openSidebar", "data");
                        setTimeout(() => EventBus.$emit("createTestimonial"), 100);
                    },
                },
                { root: true }
            );
        },
        showUnpublishedTestimonialsModal({ commit, dispatch }) {
            commit(
                "showModal",
                {
                    type: "confirm",
                    title: t("love.reviews.unpublished_title"),
                    desc: t("love.reviews.unpublished_desc"),
                    cancelBtn: t("love.reviews.unpublished_cancel"),
                    actionBtn: t("love.reviews.unpublished_publish"),
                    callback: () => {
                        setTimeout(() => {
                            dispatch("hostCode", true);
                        }, 300);
                    },
                    cancelCallback: () => {
                        setTimeout(() => {
                            dispatch("hostCode", false);
                        }, 300);
                    },
                },
                { root: true }
            );
        },
        async publishProject({ state, rootGetters, dispatch }) {
            if (!rootGetters.logged) {
                logger.trace("Cannot publish - user not logged in...");
                dispatch("showUnloggedClickModal", null, { root: true });
                return;
            }

            if (!state.testimonials.length) {
                dispatch("showNoTestimonialsModal");
                logger.trace("Cannot publish - no testimonials");
                return;
            }

            if (state.testimonials.some((testimonial) => !testimonial.isPublished)) {
                logger.trace("Cannot publish - some testimonial(s) aren't published");
                dispatch("showUnpublishedTestimonialsModal");
            } else {
                // @fix[EAS-2209]: publish autosave
                if (state.hasChanges) {
                    await dispatch("love/projects/saveSnapshot", null, { root: true });
                }
                dispatch("hostCode", false);
            }
        },
        async unpublishLove({ state, commit, dispatch }) {
            await dispatch("unpublish", { uuid: state.wall.uuid });
            commit("setUnpublished", state.wall, { root: true });
            dispatch("love/projects/saveProject", null, { root: true });
        },
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        async unpublish({ _ }, { uuid }) {
            return wallsService.publish({ uuid, publish: false });
        },
        showLinkModal({ getters, commit }) {
            const modalData = {
                type: "code",
                title: t("love.wall.published"),
                code: getters.wallIframe,
                actionBtn: t("love.wall.copy_embed"),
                cancelBtn: t("love.wall.copy_link"),
                callback: () => {
                    copy(getters.wallIframe);
                    commit("showAlert", {
                        type: "success",
                        title: t("love.wall.code_copied"),
                    });
                },
                cancelCallback: () => {
                    copy(getters.wallLink);
                    commit("showAlert", {
                        type: "success",
                        title: t("love.wall.link_copied"),
                    });
                },
            };
            commit("showModal", modalData, { root: true });
        },

        async hostCode({ state, rootState, rootGetters, commit, dispatch }, publishTestimonials) {
            const waitForChangesPromise = new Promise((resolve) => {
                EventBus.$emit("askForChanges", resolve);
            });

            const areChanges = await waitForChangesPromise;

            if (areChanges) {
                return false;
            }

            rootState.waiting = true;
            const firstPublish = !rootGetters.isPublished(state.wall);

            await nextTick();

            try {
                await wallsService.publish({ uuid: state.wall.uuid, publish: true });
                if (publishTestimonials) {
                    state.testimonials.forEach((testimonial) => {
                        testimonial.isPublished = true;
                        if (!testimonial._created && !testimonial._deleted) {
                            testimonial["_changed"] = true;
                        }
                    });
                }
                commit("setPublished", state.wall, { root: true });
                const saved = await dispatch("love/projects/saveProject", null, { root: true });
                if (!saved) {
                    return false;
                }
                if (firstPublish) {
                    dispatch("showLinkModal");
                }
                rootState.waiting = false;
                return true;
            } catch {
                rootState.waiting = false;
                return false;
            }
        },
        sendWebhook({ state }, payload) {
            const dataToSent = payload;
            dataToSent.location = window.location.href;

            const xhttp = new XMLHttpRequest();
            xhttp.open("POST", state.wall.webhookUrl, true);
            xhttp.setRequestHeader("Content-type", "application/json");
            xhttp.send(JSON.stringify(dataToSent));
        },
        changeFontFamily({ state, dispatch }, payload) {
            state.wall.fontFamily = payload;
            dispatch("love/projects/saveProject", null, { root: true });
        },
        async searchGif(_, payload) {
            try {
                return testimonialService.searchForGifs(payload);
            } catch (e) {
                console.error(e);
                return [];
            }
        },
        async getRequestEmailHtml({ state, getters, rootState }) {
            const wall = state.wall;
            const payload = {
                link: `${getters.appUrl}/review-ask/${wall.uuid}`,
                title: wall.askTitle,
                description: wall.askDesc.replace(/\n\\\n/gm, "\n\n"),
                logo: wall.askImg.startsWith("http")
                    ? wall.askImg
                    : `${rootState.globalUrl}${wall.askImg}`,
            };
            try {
                const html = await loveCartService.getRequestEmailHtml(payload);
                return html;
            } catch {
                // eslint-disable-next-line no-empty
            }
        },
    },
};
